import React, {ReactElement, ReactNode, useCallback} from 'react';
import {Text} from '../../display/text';
import {reactNodeIsOfType} from '../../../utils';
import {
   Option,
   OptionData,
   OptionGroup, OptionGroupData,
   OptionGroupProps,
   OptionProps,
   OptionsType
} from './Option';
import {getLocale} from '@unifocus/js-utils';

/*
 * These are internal utils used by Select and AutoComplete components.  These should not be exported
 * to the user.
 */

export interface LabelRendererProps {
   searchString: string;
   label: string;
}

export const LabelRenderer = (props: LabelRendererProps) => {

   const { searchString, label } = props;

   const index = label.toLowerCase().indexOf(searchString.toLowerCase());

   if (index !== -1) {

      const length = searchString.length;

      const prefix = label.substring(0, index);
      const suffix = label.substring(index + length);
      const match = label.substring(index, index + length);

      return (
         <span>
            {prefix}<Text strong={true}>{match}</Text>{suffix}
         </span>
      );
   }
   return <>
      {label}
   </>;
};

export const mapOptionChildren = (searchString: string, children: ReactNode): ReactNode => {
   return React.Children.map(children, (child, index) => {
      if (reactNodeIsOfType(child, OptionGroup)) {
         const optionGroupChild = child as ReactElement<OptionGroupProps>;
         const props = {
            key: optionGroupChild.key || index
         };
         return React.cloneElement(optionGroupChild, props, mapOptionChildren(searchString, optionGroupChild.props.children));
      }
      else if (reactNodeIsOfType(child, Option)) {
         const optionChild = child as ReactElement<OptionProps>;
         if (React.Children.count(optionChild.props.children) === 1) {
            const optionLabel = React.Children.toArray(optionChild.props.children)[0];
            if (typeof optionLabel === 'string') {
               const props = {
                  key: optionChild.key || index
               };
               return React.cloneElement(optionChild, props, <LabelRenderer searchString={searchString} label={optionLabel}/>);
            }
         }
      }
      return child;
   });
};

export const mapOptions = (searchString: string, options: OptionsType, optionLabelProp: string, isAutoComplete?: boolean) => {
   const result: OptionsType = [];

   options.forEach((option) => {
      const newOption = Object.assign({} as OptionData | OptionGroupData, option);
      if (option.options !== undefined) {
         newOption.options = mapOptions(searchString, option.options as OptionData[], optionLabelProp, isAutoComplete);
      }
      else if (isAutoComplete) {
         newOption[optionLabelProp] = <LabelRenderer searchString={searchString} label={option['value'] as string}/>;
      }
      else if (typeof option[optionLabelProp] === 'string') {
         newOption[optionLabelProp] = <LabelRenderer searchString={searchString} label={option[optionLabelProp] as string}/>;
      }
      result.push(newOption);
   });

   return result;
};

export const getFilterOptionHandler = (optionFilterProp: string): (inputValue: string, option?: OptionsType[number]) => boolean => {
   return (inputValue: string, option?: OptionsType[number]) : boolean => {
      const locale = getLocale();
      let optionValue = option !== undefined ? option[optionFilterProp] as any : undefined;
      if (optionValue && React.isValidElement(optionValue) && reactNodeIsOfType(optionValue, LabelRenderer)) {
         const labelRenderer = optionValue as ReactElement<LabelRendererProps>;
         optionValue = labelRenderer.props.label;
      }
      if (optionValue !== undefined && inputValue !== undefined) {
         const optionString = ""+optionValue;
         return optionString.toLocaleLowerCase(locale).includes(inputValue.toLocaleLowerCase(locale));
      }
      return inputValue === optionValue;
   };
};
